import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useState } from "react";

import { pickFields } from "~/utils/pick-fields";

interface LinearChartFilterContext {
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
}

const LinearChartFilterContext = createContext<LinearChartFilterContext>({
  selected: [],
  setSelected: () =>
    console.warn("Should be used in ScalesDynmaicsChartFilterProvider"),
});

const LinearChartFilterContextProvider: React.FC<
  PropsWithChildren & { defaultSelected?: string[] }
> = ({ children, defaultSelected = [] }) => {
  const [selected, setSelected] = useState(defaultSelected);

  return (
    <LinearChartFilterContext.Provider value={{ selected, setSelected }}>
      {children}
    </LinearChartFilterContext.Provider>
  );
};

const useLinearChartFilter = () => {
  const context = useContext(LinearChartFilterContext);
  if (!context) {
    throw new Error("Should be used in ScalesDynmaicsChartFilterProvider");
  }

  const toggleItem = (item: string) => {
    context.setSelected((prev) => {
      const included = prev.includes(item);
      if (included) {
        return prev.filter((i) => i !== item);
      }
      return [...prev, item];
    });
  };

  const selected = (key: string) => context.selected.includes(key);

  const filterData = useCallback(
    (data: Record<string, unknown>[], xAxisKey?: string) => {
      if (!xAxisKey) {
        return pickFields(data, context.selected);
      }
      return pickFields(data, [xAxisKey, ...context.selected]);
    },
    [context.selected],
  );

  return {
    toggleItem,
    selected,
    filterData,
  };
};

export const LinearChartFilter = {
  Provider: LinearChartFilterContextProvider,
  useLinearChartFilter,
};
